import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Signal,
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { DOCUMENT } from '@angular/common';
import { filter } from 'rxjs/operators';
import { TranslatePipe } from '@ngx-translate/core';
import { MobileBottomBarService, ViewportService } from '@ui';
import { BOTTOM_BAR_ITEMS } from '../../consts/navigation.const';
import { PanicNavigationModels } from '../../models/navigation.model';
import { MobileKeyboardService } from '../../services/mobile-keyboard.service';
import {
  injectVisibleNavigationItems,
  isNavigationItemActive,
} from '../../helpers/navigation.helper';

const BAR_HEIGHT_CSS_VAR = '--mobile-bottom-bar-height';
const BAR_HEIGHT_PX = '60px';

@Component({
  selector: 'app-mobile-bottom-bar',
  templateUrl: './mobile-bottom-bar.component.html',
  styleUrl: './mobile-bottom-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [TranslatePipe],
  host: {
    '[class.mobile-bottom-bar-hidden]': '!isBarVisible()',
  },
})
export class MobileBottomBarComponent {
  private readonly router = inject(Router);
  private readonly viewport = inject(ViewportService);
  private readonly bottomBarService = inject(MobileBottomBarService);
  private readonly mobileKeyboardService = inject(MobileKeyboardService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);

  public readonly tabs: Signal<readonly PanicNavigationModels.ResolvedItem[]> =
    injectVisibleNavigationItems(BOTTOM_BAR_ITEMS);

  public readonly isBarVisible: Signal<boolean> = computed(
    () =>
      this.viewport.isMobile() &&
      !this.bottomBarService.hidden() &&
      !this.mobileKeyboardService.isKeyboardOpen(),
  );

  constructor() {
    effect(() => {
      const root = this.document.documentElement;
      if (this.isBarVisible()) {
        root.style.setProperty(BAR_HEIGHT_CSS_VAR, BAR_HEIGHT_PX);
      } else {
        root.style.removeProperty(BAR_HEIGHT_CSS_VAR);
      }
    });
    this.destroyRef.onDestroy(() =>
      this.document.documentElement.style.removeProperty(BAR_HEIGHT_CSS_VAR),
    );
  }

  private readonly navEnd = toSignal(
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)),
  );

  public isActive(tab: PanicNavigationModels.ResolvedItem): boolean {
    this.navEnd();
    return isNavigationItemActive(this.router, tab);
  }

  public navigateTo(tab: PanicNavigationModels.ResolvedItem): void {
    void this.router.navigateByUrl(tab.path);
  }
}
